import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Chart from "./Chart";
import Button from "@/components/button";
import ChartLoading from "../Components/ChartLoading";
import EmptyContainer from "../Components/EmptyContainer";
import StorePicker from "../Components/StorePicker";
import RangePicker from "../Components/RangePicker";
import { Box, useMediaQuery } from "@mui/material";
import UpdatePriceModal from "@/modals/updatePriceModal";
import CustomizedSnackbars from "@/components/snackBar";

import { selectStores, selectLocalization } from "@/store/store/selectors";

import { isEmpty, groupBy } from "lodash";
import { useLazyQuery } from "@apollo/client";
import { PRODUCT_SEARCH } from "@/services/productService";

import {
  RANGES_FOR_PRODUCT_SEARCH,
  FUEL_PRODUCT_CODES,
  KM_IN_MILE,
  PRICE_HISTORY_LIMIT,
} from "@/constants/config";
import { MERCHANT_ID } from "@/constants/env";
import useStyles from "./styles";

const PriceMonitor = () => {
  const { t } = useTranslation();

  const classes = useStyles();

  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("sm"));

  const localization = useSelector(selectLocalization);
  const stores = useSelector(selectStores);
  const [store, setStore] = useState(null);
  const [range, setRange] = useState(RANGES_FOR_PRODUCT_SEARCH[2]);
  const [productsByCode, setProductsByCode] = useState([]);
  const [hasSearchFinished, setHasSearchFinished] = useState(false);

  const [openUpdatePrice, setOpenUpdatePrice] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");

  const storeProducts = useRef([]);
  const otherStoresProducts = useRef([]);

  const [productSearchQuery] = useLazyQuery(PRODUCT_SEARCH, {
    context: {
      headers: {
        merchantId: MERCHANT_ID,
      },
    },
    onCompleted: (response) => {
      const responseWithMileDistance = response.product_search?.map((item) => ({
        ...item,
        distance: item.distance / KM_IN_MILE,
      }));

      const groupByStoreId = groupBy(
        responseWithMileDistance,
        (product) => product.store.id
      );
      storeProducts.current =
        groupByStoreId[
          Object.keys(groupByStoreId).find((storeId) => storeId === store.id)
        ];
      otherStoresProducts.current = Object.keys(groupByStoreId)
        .filter((storeId) => storeId !== store.id)
        .map((storeId) => groupByStoreId[storeId]);

      let filteredProductsByCode = [];
      const data = groupBy(
        responseWithMileDistance,
        (product) => product.product_code
      );
      Object.keys(data).forEach((productCode) => {
        if (
          data?.[productCode]?.find((product) => product.store?.id === store.id)
        ) {
          filteredProductsByCode.push(data[productCode]);
        }
      });
      setProductsByCode(filteredProductsByCode);
      setHasSearchFinished(true);
    },
    onError: () => {
      setProductsByCode([]);
      setHasSearchFinished(true);
    },
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (!isEmpty(stores)) {
      setStore(stores[0]);
    }
  }, [stores]);

  useEffect(() => {
    if (!!store?.id) {
      productSearchQuery({
        variables: {
          product_code: FUEL_PRODUCT_CODES,
          geo: {
            location: {
              latitude: store.address?.location?.latitude,
              longitude: store.address?.location?.longitude,
            },
            range: range * KM_IN_MILE,
          },
          limit: PRICE_HISTORY_LIMIT,
        },
      });
    }
  }, [store?.id, range]);

  const onAdjustPrice = () => {
    setOpenUpdatePrice(true);
  };

  const onPriceUpdateSuccess = () => {
    setSnackMessage(t("manage_price_screen.price_updated"));
    setHasSearchFinished(false);
    productSearchQuery({
      variables: {
        product_code: FUEL_PRODUCT_CODES,
        geo: {
          location: {
            latitude: store.address?.location?.latitude,
            longitude: store.address?.location?.longitude,
          },
          range: range * KM_IN_MILE,
        },
        limit: PRICE_HISTORY_LIMIT,
      },
      fetchPolicy: "no-cache",
    });
  };

  return (
    <div>
      <Box
        display={"flex"}
        flexDirection={isDesktop ? "row" : "column"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Box display={"flex"}>
          <span className={classes.regionalText}>
            {t("price_monitor_screen.regional_price_monitor")}
          </span>
          <Box ml={5}>
            <Button
              color="secondary"
              className={classes.adjustButton}
              variant="text"
              onClick={onAdjustPrice}
            >
              {t("price_monitor_screen.adjust_price")}
            </Button>
          </Box>
        </Box>

        {!!store && (
          <Box display={"flex"}>
            <StorePicker
              stores={stores}
              store={store}
              onChange={(value) => {
                setHasSearchFinished(false);
                setStore(value);
              }}
            />
            <Box ml={4}>
              <RangePicker
                ranges={RANGES_FOR_PRODUCT_SEARCH}
                range={range}
                onChange={(value) => {
                  setHasSearchFinished(false);
                  setRange(value);
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
      <Box mt={10}>
        {hasSearchFinished ? (
          isEmpty(productsByCode) ? (
            <EmptyContainer
              description={t("price_monitor_screen.no_products")}
            />
          ) : (
            productsByCode.map((group, id) => (
              <Chart
                key={`group-${id}`}
                products={group}
                store={store}
                range={range}
                localization={localization}
              />
            ))
          )
        ) : (
          <ChartLoading />
        )}
      </Box>

      {openUpdatePrice && (
        <UpdatePriceModal
          open={openUpdatePrice}
          onClose={() => setOpenUpdatePrice(false)}
          store={store}
          products={storeProducts.current}
          otherStoresProducts={otherStoresProducts.current.slice(0, 5)}
          onUpdateSuccess={onPriceUpdateSuccess}
        />
      )}
      <CustomizedSnackbars
        open={!!snackMessage}
        onClose={() => setSnackMessage("")}
        type="success"
        message={snackMessage}
      />
    </div>
  );
};

export default PriceMonitor;
